import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Code2,
  Globe,
  Smartphone,
  Shield,
  Brain,
  CheckCircle2,
  Rocket,
  Terminal,
  Database,
  Lock,
  Wifi,
  Cpu,
  Sparkles,
  MessageSquare,
  Home,
  Search,
  PlusCircle,
  Bell,
  User,
  Heart,
  Star,
  Send,
  ChevronRight,
  RefreshCw,
  Megaphone,
  Cloud,
  Palette,
  ShoppingCart,
  Wrench,
  BarChart3,
  Server,
  Compass,
  HardDrive,
  GitBranch,
  Upload,
  Settings,
  TrendingUp,
  PieChart,
  Layers,
  Monitor,
  Package,
  CreditCard,
  Truck,
  Clock,
  Headphones,
  FileText,
  Zap
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Custom Software Animation Component
function CustomSoftwareAnimation() {
  const [activeLines, setActiveLines] = useState([0, 1, 2]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLines(prev => prev.map(line => (line + 1) % 8));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  const codeLines = [
    { indent: 0, text: 'class UserService {', color: 'text-purple-400' },
    { indent: 1, text: 'async createUser(data) {', color: 'text-blue-400' },
    { indent: 2, text: 'const user = await db.create(data);', color: 'text-green-400' },
    { indent: 2, text: 'await notify.send(user);', color: 'text-yellow-400' },
    { indent: 2, text: 'return user;', color: 'text-cyan-400' },
    { indent: 1, text: '}', color: 'text-blue-400' },
    { indent: 0, text: '}', color: 'text-purple-400' },
    { indent: 0, text: '', color: '' },
  ];

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 font-mono text-xs h-48 overflow-hidden">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>
        <span className="text-text-tertiary text-[10px]">service.ts</span>
      </div>
      {codeLines.map((line, i) => (
        <div
          key={i}
          className={`transition-all duration-300 ${activeLines.includes(i) ? 'opacity-100 bg-purple-500/10' : 'opacity-60'}`}
          style={{ paddingLeft: `${line.indent * 16}px` }}
        >
          <span className={line.color}>{line.text}</span>
          {activeLines.includes(i) && <span className="animate-pulse text-purple-400">|</span>}
        </div>
      ))}
    </div>
  );
}

// Web Development Animation Component
function WebDevAnimation() {
  const [loading, setLoading] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoading(prev => (prev + 5) % 105);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg overflow-hidden h-48">
      {/* Browser Header */}
      <div className="flex items-center gap-2 p-2 bg-[#252540] border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>
        <div className="flex-1 bg-[#1a1a2e] rounded px-3 py-1 text-[10px] text-text-tertiary flex items-center gap-2">
          <Lock size={10} className="text-green-400" />
          www.example.com
        </div>
      </div>

      {/* Website Content */}
      <div className="p-3 space-y-2">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="w-16 h-4 rounded bg-gradient-to-r from-blue-500/30 to-cyan-500/30 animate-pulse" />
          <div className="flex gap-2">
            <div className="w-10 h-3 rounded bg-white/10" />
            <div className="w-10 h-3 rounded bg-white/10" />
            <div className="w-10 h-3 rounded bg-white/10" />
          </div>
        </div>

        {/* Hero */}
        <div className="mt-4 space-y-2">
          <div
            className="h-6 rounded bg-gradient-to-r from-blue-500/40 to-transparent transition-all duration-300"
            style={{ width: `${Math.min(loading, 100)}%` }}
          />
          <div
            className="h-3 rounded bg-white/20 transition-all duration-300"
            style={{ width: `${Math.min(loading * 0.8, 80)}%` }}
          />
        </div>

        {/* Cards */}
        <div className="flex gap-2 mt-4">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="flex-1 h-12 rounded bg-gradient-to-br from-blue-500/20 to-cyan-500/10 transition-all duration-500"
              style={{
                opacity: loading > (i + 1) * 30 ? 1 : 0.3,
                transform: `translateY(${loading > (i + 1) * 30 ? 0 : 10}px)`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Mobile Development Animation Component
function MobileDevAnimation() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [floatY, setFloatY] = useState(0);

  useEffect(() => {
    // Screen rotation
    const screenInterval = setInterval(() => {
      setCurrentScreen(prev => (prev + 1) % 3);
    }, 3000);

    // Floating animation
    const floatInterval = setInterval(() => {
      setFloatY(prev => prev === 0 ? 8 : 0);
    }, 2000);

    return () => {
      clearInterval(screenInterval);
      clearInterval(floatInterval);
    };
  }, []);

  const screens = [
    { name: 'Dashboard', color: 'from-purple-500 to-fuchsia-500' },
    { name: 'Analytics', color: 'from-blue-500 to-cyan-500' },
    { name: 'Settings', color: 'from-green-500 to-emerald-500' },
  ];

  return (
    <div className="flex items-center justify-center h-48 relative">
      {/* Floating Phone */}
      <div
        className="relative transition-transform duration-1000 ease-in-out"
        style={{ transform: `translateY(${floatY}px)` }}
      >
        {/* Phone Frame */}
        <div className="w-24 h-44 rounded-[20px] bg-gradient-to-b from-gray-800 to-gray-900 p-1 shadow-2xl shadow-purple-500/20">
          <div className="w-full h-full rounded-[16px] bg-[#0a0a12] overflow-hidden relative">
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-4 bg-[#0a0a12] rounded-b-xl z-10" />

            {/* Screen Content */}
            <div className={`absolute inset-0 bg-gradient-to-br ${screens[currentScreen].color} opacity-20`} />

            {/* App UI */}
            <div className="relative z-5 p-3 pt-6">
              {/* Status indicators */}
              <div className="flex justify-between text-[8px] text-white/50 mb-3">
                <span>9:41</span>
                <div className="flex gap-1">
                  <div className="w-3 h-1.5 bg-white/50 rounded-sm" />
                </div>
              </div>

              {/* App Header */}
              <div className="text-center mb-3">
                <div className={`w-8 h-8 mx-auto rounded-xl bg-gradient-to-br ${screens[currentScreen].color} flex items-center justify-center mb-1`}>
                  <Smartphone size={14} className="text-white" />
                </div>
                <div className="text-[9px] text-white font-medium">{screens[currentScreen].name}</div>
              </div>

              {/* Content Lines */}
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-2 rounded bg-white/10 transition-all duration-500"
                    style={{
                      width: `${80 - i * 15}%`,
                      opacity: currentScreen === i ? 1 : 0.5
                    }}
                  />
                ))}
              </div>

              {/* Bottom Nav */}
              <div className="absolute bottom-3 left-3 right-3 flex justify-around">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentScreen === i
                        ? 'bg-white scale-125'
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Platform Badges */}
        <div className="absolute -left-8 top-4 flex flex-col gap-2">
          <div className="w-6 h-6 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
              <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
            </svg>
          </div>
          <div className="w-6 h-6 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-400" fill="currentColor">
              <path d="M17.523 2H6.477C5.105 2 4 3.105 4 4.477v15.046C4 20.895 5.105 22 6.477 22h11.046C18.895 22 20 20.895 20 19.523V4.477C20 3.105 18.895 2 17.523 2zM7.5 5h9c.276 0 .5.224.5.5s-.224.5-.5.5h-9c-.276 0-.5-.224-.5-.5s.224-.5.5-.5zm4.5 14c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5zm5-4H7V8h10v7z"/>
            </svg>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute -right-6 top-8 w-4 h-4 rounded-full bg-purple-500/50 animate-pulse" />
        <div className="absolute -right-4 bottom-12 w-3 h-3 rounded-full bg-fuchsia-500/50 animate-pulse" style={{ animationDelay: '500ms' }} />
        <div className="absolute -left-4 bottom-8 w-2 h-2 rounded-full bg-blue-500/50 animate-pulse" style={{ animationDelay: '1000ms' }} />
      </div>
    </div>
  );
}

// Cybersecurity Animation Component
function CybersecurityAnimation() {
  const [scanning, setScanning] = useState(0);
  const [threats, setThreats] = useState([false, false, false, false]);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanning(prev => (prev + 1) % 100);
      if (Math.random() > 0.7) {
        setThreats(prev => {
          const newThreats = [...prev];
          const randomIndex = Math.floor(Math.random() * 4);
          newThreats[randomIndex] = true;
          setTimeout(() => {
            setThreats(p => {
              const n = [...p];
              n[randomIndex] = false;
              return n;
            });
          }, 1000);
          return newThreats;
        });
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="absolute w-full h-px bg-red-500" style={{ top: `${i * 10}%` }} />
        ))}
        {[...Array(10)].map((_, i) => (
          <div key={i} className="absolute h-full w-px bg-red-500" style={{ left: `${i * 10}%` }} />
        ))}
      </div>

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"
        style={{ top: `${scanning}%` }}
      />

      {/* Shield icon */}
      <div className="flex flex-col items-center justify-center h-full relative z-10">
        <div className="relative">
          <Shield size={48} className={`${threats.some(t => t) ? 'text-red-500 animate-pulse' : 'text-green-500'} transition-colors`} />
          {threats.some(t => t) && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[8px] text-white animate-bounce">
              !
            </div>
          )}
        </div>
        <div className="mt-2 text-[10px] text-text-secondary">
          {threats.some(t => t) ? 'Threat Detected!' : 'System Secure'}
        </div>
        <div className="mt-2 flex gap-2">
          {threats.map((t, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-colors ${t ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// AI Consulting Animation Component
function AIConsultingAnimation() {
  const [typing, setTyping] = useState('');
  const [response, setResponse] = useState('');
  const fullQuestion = 'What should we do to increase sales?';
  const fullResponse = 'AI analysis: Personalized recommendations based on customer behavior...';

  useEffect(() => {
    let questionIndex = 0;
    let responseIndex = 0;
    let phase = 'question';

    const interval = setInterval(() => {
      if (phase === 'question') {
        if (questionIndex <= fullQuestion.length) {
          setTyping(fullQuestion.slice(0, questionIndex));
          questionIndex++;
        } else {
          phase = 'wait';
          setTimeout(() => {
            phase = 'response';
          }, 500);
        }
      } else if (phase === 'response') {
        if (responseIndex <= fullResponse.length) {
          setResponse(fullResponse.slice(0, responseIndex));
          responseIndex++;
        } else {
          setTimeout(() => {
            setTyping('');
            setResponse('');
            questionIndex = 0;
            responseIndex = 0;
            phase = 'question';
          }, 2000);
        }
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
          <Brain size={12} className="text-white" />
        </div>
        <span className="text-xs text-text-secondary">AI Assistant</span>
        <div className="ml-auto flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] text-green-400">Active</span>
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 space-y-2 overflow-hidden">
        {typing && (
          <div className="flex justify-end">
            <div className="bg-purple-500/20 rounded-lg rounded-br-none px-3 py-2 max-w-[80%]">
              <p className="text-xs text-text-primary">{typing}<span className="animate-pulse">|</span></p>
            </div>
          </div>
        )}

        {response && (
          <div className="flex justify-start">
            <div className="bg-violet-500/20 rounded-lg rounded-bl-none px-3 py-2 max-w-[80%]">
              <div className="flex items-center gap-1 mb-1">
                <Sparkles size={10} className="text-violet-400" />
                <span className="text-[10px] text-violet-400">AI</span>
              </div>
              <p className="text-xs text-text-primary">{response}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Digital Marketing Animation Component
function DigitalMarketingAnimation() {
  const [activeMetric, setActiveMetric] = useState(0);
  const [chartData, setChartData] = useState([30, 45, 35, 60, 50, 75, 65]);

  useEffect(() => {
    const metricInterval = setInterval(() => {
      setActiveMetric(prev => (prev + 1) % 4);
    }, 2000);

    const chartInterval = setInterval(() => {
      setChartData(prev => prev.map(v => Math.max(20, Math.min(80, v + (Math.random() - 0.4) * 15))));
    }, 1000);

    return () => {
      clearInterval(metricInterval);
      clearInterval(chartInterval);
    };
  }, []);

  const platforms = [
    { name: 'Google', color: 'bg-blue-500', letter: 'G' },
    { name: 'Meta', color: 'bg-indigo-500', letter: 'M' },
    { name: 'Amazon', color: 'bg-orange-500', letter: 'A' },
  ];

  const metrics = [
    { label: 'CTR', value: '4.2%', trend: '+12%' },
    { label: 'ROAS', value: '3.8x', trend: '+25%' },
    { label: 'CPC', value: '$0.42', trend: '-8%' },
    { label: 'Conv', value: '847', trend: '+18%' },
  ];

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1">
          {platforms.map((p, i) => (
            <div
              key={i}
              className={`w-5 h-5 rounded ${p.color} flex items-center justify-center text-[8px] font-bold text-white transition-transform duration-300 ${activeMetric === i ? 'scale-110' : 'opacity-60'}`}
            >
              {p.letter}
            </div>
          ))}
        </div>
        <div className="text-[9px] text-green-400 flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          Live
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-1 h-16 mb-3">
        {chartData.map((value, i) => (
          <div
            key={i}
            className="flex-1 bg-gradient-to-t from-green-500 to-emerald-400 rounded-t transition-all duration-500"
            style={{ height: `${value}%` }}
          />
        ))}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-4 gap-2">
        {metrics.map((m, i) => (
          <div
            key={i}
            className={`text-center p-1.5 rounded transition-all duration-300 ${activeMetric === i ? 'bg-green-500/20' : 'bg-white/5'}`}
          >
            <div className="text-[8px] text-white/50">{m.label}</div>
            <div className="text-[10px] text-white font-medium">{m.value}</div>
            <div className={`text-[7px] ${m.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
              {m.trend}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Technical Consulting Animation Component
function TechnicalConsultingAnimation() {
  const [activePhase, setActivePhase] = useState(0);
  const [connections, setConnections] = useState([false, false, false]);

  useEffect(() => {
    const phaseInterval = setInterval(() => {
      setActivePhase(prev => (prev + 1) % 4);
    }, 2000);

    const connInterval = setInterval(() => {
      setConnections(prev => {
        const next = [...prev];
        const idx = Math.floor(Math.random() * 3);
        next[idx] = !next[idx];
        return next;
      });
    }, 800);

    return () => {
      clearInterval(phaseInterval);
      clearInterval(connInterval);
    };
  }, []);

  const phases = ['Analysis', 'Architecture', 'Strategy', 'Implementation'];

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Compass size={14} className="text-amber-400" />
          <span className="text-[10px] text-white/60">Consulting Process</span>
        </div>
      </div>

      {/* Process Flow */}
      <div className="flex justify-between items-center mb-4">
        {phases.map((phase, i) => (
          <div key={i} className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-medium transition-all duration-500 ${
                activePhase >= i
                  ? 'bg-amber-500 text-white scale-110'
                  : 'bg-white/10 text-white/40'
              }`}
            >
              {i + 1}
            </div>
            <span className={`text-[7px] mt-1 transition-colors ${activePhase >= i ? 'text-amber-400' : 'text-white/30'}`}>
              {phase}
            </span>
          </div>
        ))}
      </div>

      {/* Architecture Diagram */}
      <div className="grid grid-cols-3 gap-2">
        {['Excel', 'Legacy', 'Manuel'].map((item, i) => (
          <div key={i} className="relative">
            <div className={`bg-red-500/20 rounded p-2 text-center transition-all duration-300 ${connections[i] ? 'opacity-40 scale-95' : ''}`}>
              <FileText size={12} className="text-red-400 mx-auto mb-1" />
              <span className="text-[7px] text-white/60">{item}</span>
            </div>
            {connections[i] && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-0.5 bg-green-400 animate-pulse" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-2">
        <div className="bg-green-500/20 rounded px-3 py-1.5 flex items-center gap-2">
          <Cloud size={12} className="text-green-400" />
          <span className="text-[8px] text-green-400">Modern System</span>
        </div>
      </div>
    </div>
  );
}

// DevOps & Cloud Animation Component
function DevOpsCloudAnimation() {
  const [deployProgress, setDeployProgress] = useState(0);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    const deployInterval = setInterval(() => {
      setDeployProgress(prev => (prev + 5) % 100);
    }, 200);

    const serviceInterval = setInterval(() => {
      setActiveService(prev => (prev + 1) % 3);
    }, 1500);

    return () => {
      clearInterval(deployInterval);
      clearInterval(serviceInterval);
    };
  }, []);

  const cloudServices = [
    { name: 'AWS', color: 'text-orange-400' },
    { name: 'GCP', color: 'text-blue-400' },
    { name: 'Azure', color: 'text-cyan-400' },
  ];

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Cloud Providers */}
      <div className="flex justify-center gap-3 mb-3">
        {cloudServices.map((service, i) => (
          <div
            key={i}
            className={`px-2 py-1 rounded text-[9px] font-medium transition-all duration-300 ${
              activeService === i
                ? `bg-white/10 ${service.color} scale-110`
                : 'bg-white/5 text-white/40'
            }`}
          >
            {service.name}
          </div>
        ))}
      </div>

      {/* Pipeline */}
      <div className="bg-white/5 rounded-lg p-2 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch size={10} className="text-purple-400" />
          <span className="text-[8px] text-white/60">CI/CD Pipeline</span>
        </div>
        <div className="flex gap-1">
          {['Build', 'Test', 'Deploy'].map((step, i) => (
            <div
              key={i}
              className={`flex-1 h-6 rounded flex items-center justify-center text-[7px] transition-all duration-300 ${
                deployProgress > (i + 1) * 30
                  ? 'bg-green-500/30 text-green-400'
                  : deployProgress > i * 30
                  ? 'bg-yellow-500/30 text-yellow-400 animate-pulse'
                  : 'bg-white/5 text-white/30'
              }`}
            >
              {step}
            </div>
          ))}
        </div>
      </div>

      {/* Containers */}
      <div className="grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className="bg-cyan-500/10 rounded p-1.5 flex flex-col items-center"
            style={{ opacity: 0.5 + (i * 0.15) }}
          >
            <HardDrive size={10} className="text-cyan-400 mb-1" />
            <span className="text-[6px] text-white/40">Container</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// UI/UX Design Animation Component
function UIUXDesignAnimation() {
  const [activeScreen, setActiveScreen] = useState(0);
  const [cursor, setCursor] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const screenInterval = setInterval(() => {
      setActiveScreen(prev => (prev + 1) % 3);
    }, 2500);

    const cursorInterval = setInterval(() => {
      setCursor({
        x: 20 + Math.random() * 60,
        y: 20 + Math.random() * 60
      });
    }, 1000);

    return () => {
      clearInterval(screenInterval);
      clearInterval(cursorInterval);
    };
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Design Tools */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1">
          {['#8B5CF6', '#EC4899', '#06B6D4'].map((color, i) => (
            <div
              key={i}
              className="w-4 h-4 rounded-full transition-transform duration-300 hover:scale-125"
              style={{ backgroundColor: color, opacity: activeScreen === i ? 1 : 0.4 }}
            />
          ))}
        </div>
        <Palette size={14} className="text-pink-400" />
      </div>

      {/* Design Canvas */}
      <div className="relative bg-white/5 rounded-lg h-28 overflow-hidden">
        {/* Wireframe Elements */}
        <div className="absolute inset-2 space-y-2">
          <div className="h-4 w-16 bg-purple-500/30 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-12 w-12 bg-pink-500/20 rounded" />
            <div className="flex-1 space-y-1">
              <div className="h-2 bg-white/10 rounded" style={{ width: '80%' }} />
              <div className="h-2 bg-white/10 rounded" style={{ width: '60%' }} />
              <div className="h-2 bg-white/10 rounded" style={{ width: '70%' }} />
            </div>
          </div>
          <div className="flex gap-1">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className={`flex-1 h-6 rounded transition-all duration-500 ${
                  activeScreen === i ? 'bg-cyan-500/30' : 'bg-white/5'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Cursor */}
        <div
          className="absolute w-3 h-3 transition-all duration-500 ease-out"
          style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}
        >
          <svg viewBox="0 0 24 24" className="text-white drop-shadow-lg" fill="currentColor">
            <path d="M4 4l16 8-8 2-2 8z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// E-Commerce Animation Component
function ECommerceAnimation() {
  const [cartItems, setCartItems] = useState(0);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const cartInterval = setInterval(() => {
      setCartItems(prev => (prev + 1) % 5);
    }, 1500);

    const processInterval = setInterval(() => {
      setProcessing(true);
      setTimeout(() => setProcessing(false), 1000);
    }, 3000);

    return () => {
      clearInterval(cartInterval);
      clearInterval(processInterval);
    };
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Store Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShoppingCart size={14} className="text-orange-400" />
          <span className="text-[10px] text-white/60">E-Commerce</span>
        </div>
        <div className="relative">
          <Package size={14} className="text-white/40" />
          {cartItems > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full text-[6px] flex items-center justify-center text-white">
              {cartItems}
            </span>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className={`bg-white/5 rounded p-2 transition-all duration-300 ${
              cartItems === i + 1 ? 'ring-1 ring-orange-500 scale-105' : ''
            }`}
          >
            <div className="h-8 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded mb-1" />
            <div className="h-1.5 bg-white/10 rounded w-3/4" />
            <div className="h-1.5 bg-orange-500/30 rounded w-1/2 mt-1" />
          </div>
        ))}
      </div>

      {/* Checkout */}
      <div className="flex gap-2">
        <div className="flex-1 bg-white/5 rounded p-2 flex items-center gap-2">
          <CreditCard size={12} className={`transition-colors ${processing ? 'text-green-400' : 'text-white/40'}`} />
          <div className="flex-1">
            <div className={`h-1 rounded transition-all duration-1000 ${processing ? 'bg-green-500 w-full' : 'bg-white/20 w-0'}`} />
          </div>
        </div>
        <div className="bg-white/5 rounded p-2">
          <Truck size={12} className="text-blue-400" />
        </div>
      </div>
    </div>
  );
}

// Maintenance & Support Animation Component
function MaintenanceSupportAnimation() {
  const [activeTicket, setActiveTicket] = useState(0);
  const [systemStatus, setSystemStatus] = useState([true, true, true, true]);

  useEffect(() => {
    const ticketInterval = setInterval(() => {
      setActiveTicket(prev => (prev + 1) % 3);
    }, 2000);

    const statusInterval = setInterval(() => {
      setSystemStatus(prev => {
        const next = [...prev];
        const idx = Math.floor(Math.random() * 4);
        next[idx] = Math.random() > 0.2;
        return next;
      });
    }, 1500);

    return () => {
      clearInterval(ticketInterval);
      clearInterval(statusInterval);
    };
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Headphones size={14} className="text-teal-400" />
          <span className="text-[10px] text-white/60">24/7 Support</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-[8px] text-green-400">Online</span>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-4 gap-1 mb-3">
        {['API', 'DB', 'CDN', 'Auth'].map((service, i) => (
          <div
            key={i}
            className={`rounded p-1.5 text-center transition-all duration-300 ${
              systemStatus[i] ? 'bg-green-500/10' : 'bg-red-500/10 animate-pulse'
            }`}
          >
            <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${systemStatus[i] ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className="text-[7px] text-white/60">{service}</span>
          </div>
        ))}
      </div>

      {/* Support Tickets */}
      <div className="space-y-1">
        {['Bug Fix', 'Update', 'Optimization'].map((ticket, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 p-1.5 rounded transition-all duration-300 ${
              activeTicket === i ? 'bg-teal-500/20' : 'bg-white/5'
            }`}
          >
            <Wrench size={10} className={activeTicket === i ? 'text-teal-400 animate-spin' : 'text-white/30'} />
            <span className="text-[8px] text-white/60 flex-1">{ticket}</span>
            <Clock size={8} className="text-white/30" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Data Analytics Animation Component
function DataAnalyticsAnimation() {
  const [data, setData] = useState([40, 65, 45, 80, 55, 70, 60]);
  const [activeMetric, setActiveMetric] = useState(0);

  useEffect(() => {
    const dataInterval = setInterval(() => {
      setData(prev => prev.map(v => Math.max(20, Math.min(90, v + (Math.random() - 0.5) * 20))));
    }, 1000);

    const metricInterval = setInterval(() => {
      setActiveMetric(prev => (prev + 1) % 3);
    }, 2000);

    return () => {
      clearInterval(dataInterval);
      clearInterval(metricInterval);
    };
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-indigo-400" />
          <span className="text-[10px] text-white/60">Analytics Dashboard</span>
        </div>
        <PieChart size={12} className="text-white/30" />
      </div>

      {/* Chart */}
      <div className="flex items-end gap-1 h-16 mb-3 px-1">
        {data.map((value, i) => (
          <div
            key={i}
            className="flex-1 bg-gradient-to-t from-indigo-500 to-purple-400 rounded-t transition-all duration-500"
            style={{ height: `${value}%` }}
          />
        ))}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Visitors', value: '12.4K', icon: TrendingUp, color: 'text-green-400' },
          { label: 'Conversion', value: '3.2%', icon: Zap, color: 'text-yellow-400' },
          { label: 'Revenue', value: '$84K', icon: BarChart3, color: 'text-indigo-400' },
        ].map((metric, i) => (
          <div
            key={i}
            className={`text-center p-1.5 rounded transition-all duration-300 ${
              activeMetric === i ? 'bg-indigo-500/20 scale-105' : 'bg-white/5'
            }`}
          >
            <metric.icon size={12} className={`mx-auto mb-1 ${metric.color}`} />
            <div className="text-[10px] text-white font-medium">{metric.value}</div>
            <div className="text-[7px] text-white/40">{metric.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Hosting & Domain Animation Component
function HostingDomainAnimation() {
  const [activeServer, setActiveServer] = useState(0);
  const [traffic, setTraffic] = useState([30, 50, 40, 60]);

  useEffect(() => {
    const serverInterval = setInterval(() => {
      setActiveServer(prev => (prev + 1) % 3);
    }, 2000);

    const trafficInterval = setInterval(() => {
      setTraffic(prev => prev.map(v => Math.max(20, Math.min(80, v + (Math.random() - 0.5) * 15))));
    }, 500);

    return () => {
      clearInterval(serverInterval);
      clearInterval(trafficInterval);
    };
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-lg p-4 h-48 overflow-hidden">
      {/* Domain Bar */}
      <div className="bg-white/5 rounded-lg p-2 mb-3 flex items-center gap-2">
        <Globe size={12} className="text-green-400" />
        <div className="flex-1 flex items-center">
          <span className="text-[9px] text-green-400">https://</span>
          <span className="text-[9px] text-white">yourdomain</span>
          <span className="text-[9px] text-cyan-400">.com</span>
        </div>
        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
      </div>

      {/* Servers */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {['Shared', 'VPS', 'Dedicated'].map((type, i) => (
          <div
            key={i}
            className={`rounded p-2 text-center transition-all duration-300 ${
              activeServer === i ? 'bg-cyan-500/20 scale-105' : 'bg-white/5'
            }`}
          >
            <Server size={14} className={`mx-auto mb-1 ${activeServer === i ? 'text-cyan-400' : 'text-white/30'}`} />
            <span className="text-[7px] text-white/60">{type}</span>
          </div>
        ))}
      </div>

      {/* Traffic Monitor */}
      <div className="bg-white/5 rounded p-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] text-white/40">Server Traffic</span>
          <span className="text-[8px] text-cyan-400">%{Math.round(traffic.reduce((a, b) => a + b, 0) / 4)}</span>
        </div>
        <div className="flex gap-0.5 h-4">
          {traffic.map((v, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-cyan-500 to-blue-400 rounded-sm transition-all duration-300"
              style={{ height: `${v}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Animation components mapping
const ServiceAnimations: Record<string, React.FC> = {
  'custom-software': CustomSoftwareAnimation,
  'web-development': WebDevAnimation,
  'mobile-development': MobileDevAnimation,
  'cybersecurity': CybersecurityAnimation,
  'ai-consulting': AIConsultingAnimation,
  'digital-marketing': DigitalMarketingAnimation,
  'technical-consulting': TechnicalConsultingAnimation,
  'devops-cloud': DevOpsCloudAnimation,
  'uiux-design': UIUXDesignAnimation,
  'ecommerce': ECommerceAnimation,
  'maintenance-support': MaintenanceSupportAnimation,
  'data-analytics': DataAnalyticsAnimation,
  'hosting-domain': HostingDomainAnimation,
};

// All 5 services from COMPANY_SPEC.md
const services = [
  {
    id: 'custom-software',
    title: 'Özel Yazılım Geliştirme',
    description: 'İhtiyaca özel web uygulamaları, kurumsal yazılım çözümleri ve API entegrasyonları geliştiriyoruz.',
    icon: Code2,
    color: 'from-purple-500 to-fuchsia-500',
    features: [
      'İhtiyaca özel web uygulamaları',
      'Kurumsal yazılım çözümleri',
      'Veritabanı tasarımı ve yönetimi',
      'API geliştirme ve entegrasyon',
      'Legacy sistem modernizasyonu'
    ]
  },
  {
    id: 'web-development',
    title: 'Web Geliştirme',
    description: 'E-ticaret siteleri, kurumsal web siteleri ve içerik yönetim sistemleri geliştiriyoruz.',
    icon: Globe,
    color: 'from-blue-500 to-cyan-500',
    features: [
      'E-Ticaret Siteleri (Shopify, WooCommerce, özel çözümler)',
      'Ödeme sistemi entegrasyonları',
      'Kargo entegrasyonları',
      'Kurumsal Web Siteleri',
      'Responsive tasarım',
      'SEO optimizasyonu'
    ],
    subServices: [
      {
        title: 'E-Ticaret Siteleri',
        items: ['Shopify', 'WooCommerce', 'Özel çözümler', 'Ödeme entegrasyonları']
      },
      {
        title: 'Kurumsal Web Siteleri',
        items: ['Responsive tasarım', 'SEO optimizasyonu', 'CMS', 'Çoklu dil']
      }
    ]
  },
  {
    id: 'mobile-development',
    title: 'Mobil Uygulama Geliştirme',
    description: 'Android, iOS, watchOS, Wear OS ve masaüstü uygulamalar geliştiriyoruz.',
    icon: Smartphone,
    color: 'from-green-500 to-emerald-500',
    features: [
      'Android uygulamaları',
      'iOS uygulamaları',
      'watchOS (Apple Watch)',
      'Wear OS (Android Watch)',
      'Windows masaüstü uygulamaları',
      'macOS uygulamaları'
    ],
    technologies: [
      { name: 'Native', items: ['Swift', 'Kotlin'] },
      { name: 'Cross-platform', items: ['React Native', 'Flutter'] },
      { name: 'Desktop', items: ['Tauri', 'Electron'] }
    ]
  },
  {
    id: 'cybersecurity',
    title: 'Siber Güvenlik Hizmetleri',
    description: 'Güvenlik denetimi, sızma testi ve güvenlik danışmanlığı hizmetleri sunuyoruz.',
    icon: Shield,
    color: 'from-red-500 to-orange-500',
    features: [
      'Güvenlik denetimi',
      'Sızma testi (Penetration Testing)',
      'Güvenlik açığı değerlendirmesi',
      'Güvenlik danışmanlığı',
      'Olay müdahale hizmetleri',
      'Güvenlik eğitimleri'
    ]
  },
  {
    id: 'ai-consulting',
    title: 'Yapay Zeka Danışmanlığı',
    description: 'AI strateji danışmanlığı, model entegrasyon ve özel AI çözüm geliştirme hizmetleri.',
    icon: Brain,
    color: 'from-violet-500 to-purple-500',
    features: [
      'AI strateji danışmanlığı',
      'Model seçimi ve entegrasyon',
      'Özel AI çözüm geliştirme',
      'ChatBot ve asistan geliştirme'
    ]
  },
  {
    id: 'digital-marketing',
    title: 'Dijital Reklam Yönetimi',
    description: 'AI destekli dijital reklam yönetimi, kampanya optimizasyonu ve performans analizi hizmetleri.',
    icon: Megaphone,
    color: 'from-green-500 to-teal-500',
    features: [
      'Google Ads yönetimi',
      'Amazon Ads yönetimi',
      'Meta Ads (Facebook, Instagram)',
      'Google Analytics entegrasyonu',
      'Google Search Console entegrasyonu',
      'Otomatik bütçe optimizasyonu',
      'A/B test yönetimi',
      'Performans raporları',
      'AI destekli kampanya önerileri'
    ]
  },
  {
    id: 'technical-consulting',
    title: 'Teknik Danışmanlık',
    description: 'Mimari tasarım, teknoloji seçimi ve dijital dönüşüm danışmanlığı. Excel ve legacy sistemlerden modern bulut tabanlı çözümlere geçiş.',
    icon: Compass,
    color: 'from-amber-500 to-orange-500',
    features: [
      'Yazılım mimari tasarımı',
      'Teknoloji stack seçimi',
      'Legacy sistem modernizasyonu',
      'Excel/offline sistemlerden buluta geçiş',
      'Kod audit ve review',
      'Performans optimizasyonu',
      'Teknik borç analizi',
      'Ölçeklenebilirlik planlaması'
    ]
  },
  {
    id: 'devops-cloud',
    title: 'DevOps & Cloud',
    description: 'Bulut altyapı yönetimi, CI/CD pipeline kurulumu ve konteynerizasyon çözümleri. AWS, GCP, Azure uzmanı ekibimizle.',
    icon: Cloud,
    color: 'from-cyan-500 to-blue-500',
    features: [
      'AWS, GCP, Azure yönetimi',
      'CI/CD pipeline kurulumu',
      'Docker & Kubernetes',
      'Infrastructure as Code (Terraform)',
      'Otomatik ölçeklendirme',
      'Monitoring ve alerting',
      '24/7 sistem izleme',
      'Disaster recovery planlaması'
    ]
  },
  {
    id: 'uiux-design',
    title: 'UI/UX Tasarım',
    description: 'Kullanıcı deneyimi tasarımı, arayüz tasarımı ve prototipleme. Figma, Adobe XD ile profesyonel tasarımlar.',
    icon: Palette,
    color: 'from-pink-500 to-rose-500',
    features: [
      'Kullanıcı araştırması',
      'Wireframe ve prototip',
      'UI tasarım (Figma, Adobe XD)',
      'Design system oluşturma',
      'Responsive tasarım',
      'Kullanılabilirlik testi',
      'A/B test tasarımı',
      'Marka kimliği tasarımı'
    ]
  },
  {
    id: 'ecommerce',
    title: 'E-Ticaret Çözümleri',
    description: 'Online mağaza kurulumu, ödeme sistemleri entegrasyonu ve pazar yeri bağlantıları.',
    icon: ShoppingCart,
    color: 'from-orange-500 to-amber-500',
    features: [
      'E-ticaret sitesi geliştirme',
      'Shopify, WooCommerce kurulumu',
      'Ödeme sistemi entegrasyonu (iyzico, PayTR)',
      'Kargo entegrasyonları',
      'Trendyol, Hepsiburada, N11 entegrasyonu',
      'Stok yönetim sistemi',
      'Sipariş takip sistemi',
      'Müşteri paneli geliştirme'
    ]
  },
  {
    id: 'maintenance-support',
    title: 'Bakım & Destek',
    description: 'SLA bazlı teknik destek, 7/24 sistem izleme ve proaktif bakım hizmetleri.',
    icon: Wrench,
    color: 'from-teal-500 to-emerald-500',
    features: [
      '7/24 teknik destek',
      'SLA bazlı hizmet seviyeleri',
      'Proaktif sistem izleme',
      'Güvenlik güncellemeleri',
      'Performans optimizasyonu',
      'Yedekleme yönetimi',
      'Acil müdahale hizmeti',
      'Aylık raporlama'
    ]
  },
  {
    id: 'data-analytics',
    title: 'Veri Analitiği & BI',
    description: 'İş zekası çözümleri, veri görselleştirme ve özel dashboard geliştirme.',
    icon: BarChart3,
    color: 'from-indigo-500 to-violet-500',
    features: [
      'Özel dashboard geliştirme',
      'Veri görselleştirme',
      'Power BI, Tableau entegrasyonu',
      'ETL pipeline kurulumu',
      'Gerçek zamanlı analitik',
      'KPI takip sistemleri',
      'Tahmine dayalı analiz',
      'Raporlama otomasyonu'
    ]
  },
  {
    id: 'hosting-domain',
    title: 'Hosting & Sunucu',
    description: 'Alan adı tescili, web hosting, VPS ve dedicated sunucu çözümleri.',
    icon: Server,
    color: 'from-slate-500 to-gray-600',
    features: [
      'Alan adı tescili (.com, .com.tr, vb.)',
      'Paylaşımlı hosting paketleri',
      'VPS sunucu çözümleri',
      'Dedicated sunucu kiralama',
      'SSL sertifikası kurulumu',
      'E-posta hosting',
      'DNS yönetimi',
      'Sunucu bakım ve yönetimi'
    ]
  }
];

// Process steps
const processSteps = [
  { step: 1, title: 'Keşif', description: 'İhtiyaçlarınızı analiz ediyor, proje kapsamını belirliyoruz.' },
  { step: 2, title: 'Planlama', description: 'Detaylı proje planı ve teknik mimari tasarlıyoruz.' },
  { step: 3, title: 'Geliştirme', description: 'Agile metodoloji ile iteratif geliştirme yapıyoruz.' },
  { step: 4, title: 'Test', description: 'Kapsamlı test süreçleri ile kaliteyi garanti ediyoruz.' },
  { step: 5, title: 'Yayın', description: 'Projeyi production ortamına deploy ediyoruz.' },
  { step: 6, title: 'Destek', description: 'Sürekli bakım ve destek hizmeti sağlıyoruz.' }
];

// Technologies
const technologies = [
  'React', 'Next.js', 'Vue', 'Angular', 'Node.js', 'Python', 'Go', 'Rust',
  'Swift', 'Kotlin', 'Flutter', 'React Native', 'Tauri', 'Electron',
  'PostgreSQL', 'MongoDB', 'Redis', 'Docker', 'Kubernetes', 'AWS', 'GCP', 'Azure'
];

export function ServicesPage() {
  const { t } = useTranslation();
  const location = useLocation();

  // Translated services data
  const translatedServices = [
    {
      id: 'custom-software',
      title: t('services.items.custom-software.title'),
      description: t('services.items.custom-software.description'),
      icon: Code2,
      color: 'from-purple-500 to-fuchsia-500',
      features: t('services.items.custom-software.features', { returnObjects: true }) as string[]
    },
    {
      id: 'web-development',
      title: t('services.items.web-development.title'),
      description: t('services.items.web-development.description'),
      icon: Globe,
      color: 'from-blue-500 to-cyan-500',
      features: t('services.items.web-development.features', { returnObjects: true }) as string[],
      subServices: [
        {
          title: t('services.items.web-development.subServices.ecommerce.title'),
          items: t('services.items.web-development.subServices.ecommerce.items', { returnObjects: true }) as string[]
        },
        {
          title: t('services.items.web-development.subServices.corporate.title'),
          items: t('services.items.web-development.subServices.corporate.items', { returnObjects: true }) as string[]
        }
      ]
    },
    {
      id: 'mobile-development',
      title: t('services.items.mobile-development.title'),
      description: t('services.items.mobile-development.description'),
      icon: Smartphone,
      color: 'from-green-500 to-emerald-500',
      features: t('services.items.mobile-development.features', { returnObjects: true }) as string[],
      technologies: [
        { name: t('services.items.mobile-development.technologies.native.name'), items: t('services.items.mobile-development.technologies.native.items', { returnObjects: true }) as string[] },
        { name: t('services.items.mobile-development.technologies.crossPlatform.name'), items: t('services.items.mobile-development.technologies.crossPlatform.items', { returnObjects: true }) as string[] },
        { name: t('services.items.mobile-development.technologies.desktop.name'), items: t('services.items.mobile-development.technologies.desktop.items', { returnObjects: true }) as string[] }
      ]
    },
    {
      id: 'cybersecurity',
      title: t('services.items.cybersecurity.title'),
      description: t('services.items.cybersecurity.description'),
      icon: Shield,
      color: 'from-red-500 to-orange-500',
      features: t('services.items.cybersecurity.features', { returnObjects: true }) as string[]
    },
    {
      id: 'ai-consulting',
      title: t('services.items.ai-consulting.title'),
      description: t('services.items.ai-consulting.description'),
      icon: Brain,
      color: 'from-violet-500 to-purple-500',
      features: t('services.items.ai-consulting.features', { returnObjects: true }) as string[]
    },
    {
      id: 'digital-marketing',
      title: t('services.items.digital-marketing.title'),
      description: t('services.items.digital-marketing.description'),
      icon: Megaphone,
      color: 'from-green-500 to-teal-500',
      features: t('services.items.digital-marketing.features', { returnObjects: true }) as string[]
    },
    {
      id: 'technical-consulting',
      title: t('services.items.technical-consulting.title'),
      description: t('services.items.technical-consulting.description'),
      icon: Compass,
      color: 'from-amber-500 to-orange-500',
      features: t('services.items.technical-consulting.features', { returnObjects: true }) as string[]
    },
    {
      id: 'devops-cloud',
      title: t('services.items.devops-cloud.title'),
      description: t('services.items.devops-cloud.description'),
      icon: Cloud,
      color: 'from-cyan-500 to-blue-500',
      features: t('services.items.devops-cloud.features', { returnObjects: true }) as string[]
    },
    {
      id: 'uiux-design',
      title: t('services.items.uiux-design.title'),
      description: t('services.items.uiux-design.description'),
      icon: Palette,
      color: 'from-pink-500 to-rose-500',
      features: t('services.items.uiux-design.features', { returnObjects: true }) as string[]
    },
    {
      id: 'ecommerce',
      title: t('services.items.ecommerce.title'),
      description: t('services.items.ecommerce.description'),
      icon: ShoppingCart,
      color: 'from-orange-500 to-amber-500',
      features: t('services.items.ecommerce.features', { returnObjects: true }) as string[]
    },
    {
      id: 'maintenance-support',
      title: t('services.items.maintenance-support.title'),
      description: t('services.items.maintenance-support.description'),
      icon: Wrench,
      color: 'from-teal-500 to-emerald-500',
      features: t('services.items.maintenance-support.features', { returnObjects: true }) as string[]
    },
    {
      id: 'data-analytics',
      title: t('services.items.data-analytics.title'),
      description: t('services.items.data-analytics.description'),
      icon: BarChart3,
      color: 'from-indigo-500 to-violet-500',
      features: t('services.items.data-analytics.features', { returnObjects: true }) as string[]
    },
    {
      id: 'hosting-domain',
      title: t('services.items.hosting-domain.title'),
      description: t('services.items.hosting-domain.description'),
      icon: Server,
      color: 'from-slate-500 to-gray-600',
      features: t('services.items.hosting-domain.features', { returnObjects: true }) as string[]
    }
  ];

  // Translated process steps
  const translatedProcessSteps = [
    { step: 1, title: t('services.process.steps.1.title'), description: t('services.process.steps.1.description') },
    { step: 2, title: t('services.process.steps.2.title'), description: t('services.process.steps.2.description') },
    { step: 3, title: t('services.process.steps.3.title'), description: t('services.process.steps.3.description') },
    { step: 4, title: t('services.process.steps.4.title'), description: t('services.process.steps.4.description') },
    { step: 5, title: t('services.process.steps.5.title'), description: t('services.process.steps.5.description') },
    { step: 6, title: t('services.process.steps.6.title'), description: t('services.process.steps.6.description') }
  ];

  // Scroll to hash on page load or hash change
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure the page is rendered
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          const headerOffset = 100; // Account for fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location.hash]);

  return (
    <>
      <SEO {...SEO_CONFIG.services} />
      <div>
      {/* Hero Section */}
      <section className="section-lg pt-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5" />
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-xl mb-6">
              {t('services.hero.title')}
            </h1>
            <p className="text-lg text-text-secondary mb-8">
              {t('services.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="space-y-16">
            {translatedServices.map((service, index) => {
              const AnimationComponent = ServiceAnimations[service.id];

              return (
                <div
                  key={service.id}
                  id={service.id}
                  className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
                >
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.color} p-4 mb-6`}>
                      <service.icon className="w-full h-full text-white" />
                    </div>
                    <h2 className="heading-lg mb-4">{service.title}</h2>
                    <p className="text-text-secondary mb-6">{service.description}</p>

                    <ul className="space-y-3 mb-6">
                      {service.features.slice(0, 6).map((feature) => (
                        <li key={feature} className="flex items-center gap-3 text-text-secondary">
                          <CheckCircle2 size={18} className="text-purple-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {service.technologies && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-text-primary mb-3">{t('services.technologies.title')}:</h4>
                        <div className="flex flex-wrap gap-2">
                          {service.technologies.map((tech) => (
                            <div key={tech.name} className="text-xs">
                              <span className="text-purple-400 font-medium">{tech.name}:</span>{' '}
                              <span className="text-text-secondary">{tech.items.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                    <div className="card border-purple-500/20 p-4">
                      {/* Live Animation */}
                      {AnimationComponent && <AnimationComponent />}

                      {/* Tags */}
                      <div className="mt-4">
                        {service.subServices ? (
                          <div className="space-y-3">
                            {service.subServices.map((sub) => (
                              <div key={sub.title}>
                                <h4 className="font-medium text-text-primary text-sm mb-2">{sub.title}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {sub.items.map((item) => (
                                    <span key={item} className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {service.features.slice(0, 4).map((feature) => (
                              <span key={feature} className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">
                                {feature}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - Timeline Design */}
      <section className="section bg-bg-surface relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">{t('services.process.title')}</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('services.process.description')}
            </p>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block relative">
            {/* Connection Line */}
            <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-purple-500" />

            <div className="grid grid-cols-6 gap-4">
              {translatedProcessSteps.map((item, index) => (
                <div key={item.step} className="relative flex flex-col items-center">
                  {/* Step Circle */}
                  <div className="relative z-10 mb-6">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                      <span className="text-white font-bold text-lg">{item.step}</span>
                    </div>
                    {/* Pulse ring */}
                    <div className="absolute inset-0 rounded-full bg-purple-500/30 animate-ping" style={{ animationDuration: `${2 + index * 0.3}s` }} />
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="font-semibold text-text-primary mb-2">{item.title}</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile/Tablet Timeline */}
          <div className="lg:hidden relative">
            {/* Vertical Line */}
            <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-fuchsia-500 to-purple-500" />

            <div className="space-y-8">
              {translatedProcessSteps.map((item, index) => (
                <div key={item.step} className="relative flex gap-6 items-start">
                  {/* Step Circle */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                      <span className="text-white font-bold text-lg">{item.step}</span>
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 card border-purple-500/20 p-4">
                    <h3 className="font-semibold text-text-primary mb-2">{item.title}</h3>
                    <p className="text-text-secondary text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies - Animated Background */}
      <section className="section relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Floating Orbs */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />

          {/* Floating Particles */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-purple-500/30 rounded-full animate-bounce"
              style={{
                left: `${10 + (i * 7.5)}%`,
                top: `${20 + Math.sin(i) * 30}%`,
                animationDuration: `${2 + (i % 3)}s`,
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('services.technologies.title').split(' ')[0]}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('services.technologies.title').split(' ').slice(1).join(' ')}
              </span>
            </h2>
            <p className="text-text-secondary">{t('services.technologies.description')}</p>
          </div>

          {/* Technology Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: t('services.technologies.categories.frontend'), techs: ['React', 'Next.js', 'Vue', 'Angular'], color: 'from-blue-500 to-cyan-500' },
              { title: t('services.technologies.categories.backend'), techs: ['Node.js', 'Python', 'Go', 'Rust'], color: 'from-green-500 to-emerald-500' },
              { title: t('services.technologies.categories.mobile'), techs: ['Swift', 'Kotlin', 'Flutter', 'React Native'], color: 'from-orange-500 to-amber-500' },
              { title: t('services.technologies.categories.devops'), techs: ['Docker', 'Kubernetes', 'AWS', 'GCP'], color: 'from-purple-500 to-fuchsia-500' },
            ].map((category) => (
              <div key={category.title} className="card border-purple-500/10 hover:border-purple-500/30 transition-colors group">
                <div className={`text-sm font-semibold bg-gradient-to-r ${category.color} bg-clip-text text-transparent mb-3`}>
                  {category.title}
                </div>
                <div className="flex flex-wrap gap-2">
                  {category.techs.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1.5 rounded-lg bg-white/5 text-text-secondary text-sm hover:bg-purple-500/10 hover:text-purple-400 transition-all cursor-default"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* All Technologies Cloud */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/5 to-transparent rounded-2xl" />
            <div className="flex flex-wrap justify-center gap-3 p-6">
              {technologies.map((tech, index) => (
                <span
                  key={tech}
                  className="px-4 py-2 rounded-full bg-bg-surface/80 backdrop-blur border border-border-default text-text-secondary hover:border-purple-500/50 hover:text-purple-400 hover:scale-105 transition-all cursor-default"
                  style={{
                    animationDelay: `${index * 0.05}s`
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-lg bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-4">
              {t('services.cta.title')}
            </h2>
            <p className="text-text-secondary mb-8">
              {t('services.cta.description')}
            </p>
            <Link to="/contact" className="btn btn-primary btn-lg">
              <Rocket size={20} />
              {t('services.cta.button')}
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
